import './UpdateExpSection.scss';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { useCallback, useMemo } from 'react';
import GameCard from '../../components/GameCard/GameCard';
import { GameExperience } from '../../models/survey';
import { PiTimer } from 'react-icons/pi';
import cn from 'classnames';
import { needsGameExperienceUpdate } from '../../utils/generic';

interface Props {
  title: string;
  description: string;
  values?: GameExperience[];
  setFormModified: (value: boolean) => void;
  onEdit: (gameExperience: GameExperience) => void;
}

const UpdateExpSection = ({
  title,
  description,
  values,
  setFormModified,
  onEdit
}: Props) => {
  const filteredGamesToUpdate = useMemo(
    () =>
      values?.filter((cg: any) => cg.played && needsGameExperienceUpdate(cg)) ??
      [],
    [values]
  );

  const hasGamesNotPlayed = useMemo(
    () => (values?.filter((cg) => !cg.played).length ?? 0) > 0,
    [values]
  );

  const handleEdit = useCallback(
    (cg: GameExperience) => {
      setFormModified(true);
      onEdit(cg);
    },
    [setFormModified, onEdit]
  );

  return (
    <>
      <div className="header">
        <PiTimer className="exp-icon" />
        <h3>{title}</h3>
      </div>
      <div className="exp-desc">{description}</div>
      <div
        id="carouselGamesPlayed"
        className="carousel slide mt-4"
        data-bs-ride="false"
      >
        <div className="carousel-inner">
          {filteredGamesToUpdate.map((cg: any, index: number) => (
            <div
              className={cn('carousel-item', index === 0 ? 'active' : '') ?? []}
              key={cg.id}
            >
              <div className="grid d-flex justify-content-center pb-5">
                <GameCard
                  key={cg.id}
                  gameExperience={cg}
                  onEdit={() => handleEdit(cg)}
                />
              </div>
            </div>
          ))}
          <div className="carousel-indicators">
            {filteredGamesToUpdate.map((cg: any, index: number) => (
              <button
                key={cg.id}
                type="button"
                data-bs-target="#carouselGamesPlayed"
                data-bs-slide-to={index}
                className={cn(index === 0 ? 'active' : '')}
                aria-current={index === 0 ? 'true' : 'false'}
                aria-label={cg.game?.name}
              ></button>
            ))}
          </div>
        </div>
        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#carouselGamesPlayed"
          data-bs-slide="prev"
        >
          <MdNavigateBefore className="prev-icon" aria-hidden="true" />
        </button>
        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#carouselGamesPlayed"
          data-bs-slide="next"
        >
          <MdNavigateNext className="next-icon" aria-hidden="true" />
        </button>
      </div>
      <div
        id="gridGamesPlayed"
        className={cn('grid', { 'line-bottom': hasGamesNotPlayed })}
      >
        {filteredGamesToUpdate.map((cg: any) => (
          <GameCard
            key={cg.id}
            gameExperience={cg}
            onEdit={() => {
              setFormModified(true);
              onEdit(cg);
            }}
          />
        )) ?? []}
      </div>
    </>
  );
};

export default UpdateExpSection;
